import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type SectionCardTone = 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';

let nextHeadingId = 0;

/**
 * The titled card every dashboard section sits in: a bordered surface with a
 * header (icon tile, title, optional count/subtitle, `[actions]` on the right)
 * above the content.
 *
 * The header never paints a background of its own and inherits the card's top
 * corners, so it cannot show square corners on the rounded card. The card does
 * not clip (`overflow` stays visible) because PrimeNG renders dropdowns such as
 * the paginator's rows-per-page select inline. Set `flush` for a table or list
 * that should run edge to edge; wrap any padded content next to it in
 * `.app-card-pad`.
 *
 * Project `[badge]` content (a version tag, a status) after the title and
 * `[actions]` (compact icon buttons) into the header's right-hand side.
 */
@Component({
    selector: 'app-section-card',
    standalone: true,
    template: `
        @if (title()) {
            <header class="app-section-card__header">
                @if (icon()) {
                    <span class="app-section-card__icon" aria-hidden="true"><i [class]="icon()"></i></span>
                }
                <div class="app-section-card__heading">
                    <h2 class="app-section-card__title" [id]="headingId">
                        <span class="app-section-card__title-text">{{ title() }}</span>
                        @if (count() !== null) {
                            <span class="app-section-card__count">{{ count() }}</span>
                        }
                        <ng-content select="[badge]" />
                    </h2>
                    @if (subtitle()) {
                        <p class="app-section-card__subtitle">{{ subtitle() }}</p>
                    }
                </div>
                <div class="app-section-card__actions">
                    <ng-content select="[actions]" />
                </div>
            </header>
        }
        <div class="app-section-card__body">
            <ng-content />
        </div>
    `,
    styleUrl: './section-card.component.scss',
    host: {
        class: 'app-section-card',
        '[class.app-section-card--flush]': 'flush()',
        '[class.app-section-card--titled]': '!!title()',
        '[attr.data-tone]': 'tone()',
        '[attr.role]': 'title() ? "region" : null',
        '[attr.aria-labelledby]': 'title() ? headingId : null'
    },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SectionCardComponent {
    title = input<string | null>(null);
    subtitle = input<string | null>(null);
    /** Icon class (Font Awesome or PrimeIcons) shown in the tinted tile. */
    icon = input<string | null>(null);
    /** Tile colour: `primary` for ordinary sections, a status tone for errors and warnings. */
    tone = input<SectionCardTone>('primary');
    count = input<number | null>(null);
    /** Remove the body padding so a table or list meets the card edges. */
    flush = input(false);

    readonly headingId = `app-section-card-${nextHeadingId++}`;
}
